import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Snackbar,
  Alert,
  Checkbox,
} from '@mui/material';
import { useAxiosClient } from '../utils/useAxiosClient';
import { useDevAuth } from '../utils/useDevAuth';
import { Link, useNavigate } from 'react-router-dom';

interface Organization {
  id: number;
  name: string;
  masterSheetUrl?: string;
  backendClientName?: string;
  deiTargetPillar?: string;
  dateCreated: string;
  onboardingStatus: string;
  dashboardStatus: string;
  dashboardType: string;
  trialWall: boolean;
  trialDuration: number | null;
  timezone: string;
  priority: number | null;
  isCollecting: boolean;
  skipComments: boolean;
  isDeploying: boolean;
  platformFrequencies: Record<string, string> | null;
  platformAccounts: Record<string, number>;
}

const PLATFORM_LABELS: Record<string, string> = {
  facebook: 'FB', instagram: 'IG', linkedin: 'LI',
  tiktok: 'TK', twitter: 'TW', youtube: 'YT',
};

const ALL_PLATFORMS = ['facebook', 'instagram', 'linkedin', 'tiktok', 'twitter', 'youtube'];

// Common US timezones for the dropdown
const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'UTC', label: 'UTC' },
];

const AdminOrganizations: React.FC = () => {
  const axiosClient = useAxiosClient();
  const { user } = useDevAuth();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [openOrgDialog, setOpenOrgDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState<{ key: keyof Organization; direction: 'asc' | 'desc' } | null>(null);
  const [openPriorityDialog, setOpenPriorityDialog] = useState(false);
  const [priorityEdits, setPriorityEdits] = useState<Record<number, string>>({});
  const [savingPriorities, setSavingPriorities] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [newOrg, setNewOrg] = useState({
    name: '',
    masterSheetUrl: '',
    backendClientName: '',
    deiTargetPillar: 'All',
    dashboardType: 'standard',
    trialWall: false,
    trialDuration: null as number | null,
    timezone: 'America/New_York',
  });

  // Check if user is admin: specific gmail or any @crant.ai via Google login
  const provider = user?.sub?.split('|')[0];
  const emailLower = user?.email?.toLowerCase() || '';
  const isAdmin = (provider === 'google-oauth2' || provider === 'clerk') && (emailLower === 'weezilla@gmail.com' || emailLower.endsWith('@crant.ai'));

  useEffect(() => {
    if (!isAdmin) return;

    const fetchData = async () => {
      try {
        const orgsResponse = await axiosClient.get('/api/admin/organizations');
        setOrganizations(orgsResponse.data.organizations);
      } catch (error) {
        console.error('Error fetching admin data:', error);
      }
    };

    fetchData();
  }, [isAdmin, axiosClient]);

  const handleCreateOrg = async () => {
    try {
      const response = await axiosClient.post('/api/admin/organizations', newOrg);
      setOrganizations([...organizations, response.data]);
      setOpenOrgDialog(false);
      setNewOrg({
        name: '',
        masterSheetUrl: '',
        backendClientName: '',
        deiTargetPillar: 'All',
        dashboardType: 'standard',
        trialWall: false,
        trialDuration: null,
        timezone: 'America/New_York',
      });
      setSnackbar({ open: true, message: `Created "${response.data.name}" (backend name: ${response.data.backendClientName})`, severity: 'success' });
    } catch (error: any) {
      console.error('Error creating organization:', error);
      setSnackbar({ open: true, message: error.response?.data?.detail?.error || 'Failed to create organization', severity: 'error' });
    }
  };

  const openPriorityEditor = () => {
    const edits: Record<number, string> = {};
    organizations.forEach(org => {
      edits[org.id] = org.priority != null ? String(org.priority) : '';
    });
    setPriorityEdits(edits);
    setOpenPriorityDialog(true);
  };

  const handleSavePriorities = async () => {
    setSavingPriorities(true);
    try {
      const priorities: Record<string, number | null> = {};
      for (const [idStr, val] of Object.entries(priorityEdits)) {
        priorities[idStr] = val.trim() === '' ? null : parseInt(val, 10);
      }
      await axiosClient.put('/api/admin/organizations/bulk-priorities', { priorities });
      setOrganizations(prev => prev.map(org => ({
        ...org,
        priority: priorities[String(org.id)] !== undefined ? priorities[String(org.id)] : org.priority,
      })));
      setOpenPriorityDialog(false);
      setSnackbar({ open: true, message: 'Priorities saved', severity: 'success' });
    } catch (error) {
      console.error('Error saving priorities:', error);
      setSnackbar({ open: true, message: 'Failed to save priorities', severity: 'error' });
    } finally {
      setSavingPriorities(false);
    }
  };

  const handleQuickToggle = async (orgId: number, field: 'isCollecting' | 'skipComments' | 'isDeploying', value: boolean) => {
    try {
      await axiosClient.patch(`/api/admin/organizations/${orgId}/quick-toggle`, { field, value });
      setOrganizations(prev => prev.map(o =>
        o.id === orgId ? { ...o, [field]: value } : o
      ));
    } catch (error) {
      console.error('Error toggling:', error);
      setSnackbar({ open: true, message: 'Failed to update', severity: 'error' });
    }
  };

  const handleTogglePlatform = async (orgId: number, platform: string, enabled: boolean) => {
    try {
      const res = await axiosClient.patch(`/api/admin/organizations/${orgId}/platform-toggle`, { platform, enabled });
      setOrganizations(prev => prev.map(o =>
        o.id === orgId ? { ...o, platformFrequencies: res.data.platformFrequencies } : o
      ));
    } catch (error) {
      console.error('Error toggling platform:', error);
      setSnackbar({ open: true, message: 'Failed to toggle platform', severity: 'error' });
    }
  };

  // Helper for sorting
  const handleSort = (key: keyof Organization) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const displayedOrgs = useMemo(() => {
    let data = organizations;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      data = data.filter(org => (org.name || '').toLowerCase().includes(term));
    }
    if (platformFilter !== 'all') {
      data = data.filter(org => (org.platformAccounts?.[platformFilter] || 0) > 0);
    }
    if (sortConfig) {
      data = [...data].sort((a, b) => {
        const aRaw = a[sortConfig.key];
        const bRaw = b[sortConfig.key];
        // Nulls always sort to bottom
        if (aRaw == null && bRaw == null) return 0;
        if (aRaw == null) return 1;
        if (bRaw == null) return -1;
        let aVal: any = aRaw;
        let bVal: any = bRaw;
        if (sortConfig.key === 'dateCreated') {
          aVal = new Date(aVal).getTime();
          bVal = new Date(bVal).getTime();
        } else {
          aVal = typeof aVal === 'string' ? aVal.toLowerCase() : aVal;
          bVal = typeof bVal === 'string' ? bVal.toLowerCase() : bVal;
        }
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return data;
  }, [organizations, searchTerm, platformFilter, sortConfig]);

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4">Unauthorized</Typography>
        <Typography>You do not have access to this page.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button
        variant="text"
        onClick={() => navigate('/admin')}
        sx={{ mb: 2 }}
      >
        ← Back to Admin Dashboard
      </Button>
      <Typography variant="h4" sx={{ mb: 3 }}>Organizations</Typography>

      {/* Organizations Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h5">Organizations</Typography>
          <TextField
            size="small"
            placeholder="Search by name…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ maxWidth: 300 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Platform</InputLabel>
            <Select
              value={platformFilter}
              label="Platform"
              onChange={(e) => setPlatformFilter(e.target.value)}
            >
              <MenuItem value="all">All Platforms</MenuItem>
              {ALL_PLATFORMS.map(p => (
                <MenuItem key={p} value={p} sx={{ textTransform: 'capitalize' }}>{p}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={openPriorityEditor}>
              Edit Priorities
            </Button>
            <Button variant="contained" onClick={() => setOpenOrgDialog(true)}>
              Add Organization
            </Button>
          </Box>
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sortDirection={sortConfig?.key === 'priority' ? sortConfig.direction : false}>
                  <TableSortLabel
                    active={sortConfig?.key === 'priority'}
                    direction={sortConfig?.key === 'priority' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('priority')}
                  >
                    Pri
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortConfig?.key === 'id' ? sortConfig.direction : false}>
                  <TableSortLabel
                    active={sortConfig?.key === 'id'}
                    direction={sortConfig?.key === 'id' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortConfig?.key === 'name' ? sortConfig.direction : false}>
                  <TableSortLabel
                    active={sortConfig?.key === 'name'}
                    direction={sortConfig?.key === 'name' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortConfig?.key === 'backendClientName' ? sortConfig.direction : false}>
                  <TableSortLabel
                    active={sortConfig?.key === 'backendClientName'}
                    direction={sortConfig?.key === 'backendClientName' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('backendClientName')}
                  >
                    Backend Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ px: 1, textAlign: 'center' }}>Collecting</TableCell>
                <TableCell sx={{ px: 1, textAlign: 'center' }}>Comments</TableCell>
                <TableCell sx={{ px: 1, textAlign: 'center' }} title="Included in the nightly data deploy (deploy.py production deploy-configured)">Deploying</TableCell>
                {ALL_PLATFORMS.map(p => (
                  <TableCell key={p} sx={{ px: 1, textAlign: 'center', minWidth: 50 }}>
                    {PLATFORM_LABELS[p]}
                  </TableCell>
                ))}
                <TableCell sortDirection={sortConfig?.key === 'dashboardStatus' ? sortConfig.direction : false}>
                  <TableSortLabel
                    active={sortConfig?.key === 'dashboardStatus'}
                    direction={sortConfig?.key === 'dashboardStatus' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('dashboardStatus')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedOrgs.map((org) => {
                const freqs = org.platformFrequencies || {};
                const isOff = !org.isCollecting;
                const rowSx = isOff ? { opacity: 0.45 } : {};
                return (
                  <TableRow key={org.id} sx={rowSx}>
                    <TableCell>{org.priority ?? '—'}</TableCell>
                    <TableCell>{org.id}</TableCell>
                    <TableCell>
                      <Link to={`/admin/org/${org.id}`} style={{ textDecoration: 'none', color: isOff ? '#90a4ae' : '#1976d2' }}>
                        {org.name}
                      </Link>
                    </TableCell>
                    <TableCell>{org.backendClientName}</TableCell>
                    <TableCell sx={{ px: 0, textAlign: 'center', opacity: isOff ? 1 / 0.45 : 1 }}>
                      <Checkbox
                        size="small"
                        checked={!!org.isCollecting}
                        onChange={(e) => handleQuickToggle(org.id, 'isCollecting', e.target.checked)}
                      />
                    </TableCell>
                    <TableCell sx={{ px: 0, textAlign: 'center' }}>
                      <Checkbox
                        size="small"
                        checked={!org.skipComments}
                        onChange={(e) => handleQuickToggle(org.id, 'skipComments', !e.target.checked)}
                      />
                    </TableCell>
                    <TableCell sx={{ px: 0, textAlign: 'center', opacity: isOff ? 1 / 0.45 : 1 }}>
                      <Checkbox
                        size="small"
                        color="secondary"
                        checked={!!org.isDeploying}
                        onChange={(e) => handleQuickToggle(org.id, 'isDeploying', e.target.checked)}
                      />
                    </TableCell>
                    {ALL_PLATFORMS.map(p => {
                      const count = org.platformAccounts?.[p] || 0;
                      const isEnabled = freqs[p] && freqs[p] !== 'disabled';
                      return (
                        <TableCell key={p} sx={{ px: 1, textAlign: 'center' }}>
                          {count > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <Checkbox
                                size="small"
                                checked={!!isEnabled}
                                onChange={() => handleTogglePlatform(org.id, p, !isEnabled)}
                                sx={{ p: 0 }}
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                                {count}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                      );
                    })}
                    <TableCell>{org.dashboardStatus}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Add Organization Dialog */}
      <Dialog open={openOrgDialog} onClose={() => setOpenOrgDialog(false)}>
        <DialogTitle>Add Organization</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Organization Name"
                value={newOrg.name}
                onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Master Sheet URL"
                value={newOrg.masterSheetUrl}
                onChange={(e) => setNewOrg({ ...newOrg, masterSheetUrl: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Backend Client Name"
                placeholder="auto-generated from the name"
                helperText="Leave blank to generate it (e.g. Visit Orlando becomes visit-orlando). Lowercase letters, digits and hyphens; it cannot change once data is collected. New organizations are set to deploy to the app."
                value={newOrg.backendClientName}
                onChange={(e) => setNewOrg({ ...newOrg, backendClientName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>DEI Target Pillar</InputLabel>
                <Select
                  value={newOrg.deiTargetPillar}
                  label="DEI Target Pillar"
                  onChange={(e) => setNewOrg({ ...newOrg, deiTargetPillar: e.target.value })}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="DEI">DEI</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Dashboard Type</InputLabel>
                <Select
                  value={newOrg.dashboardType}
                  label="Dashboard Type"
                  onChange={(e) => setNewOrg({ ...newOrg, dashboardType: e.target.value })}
                >
                  <MenuItem value="standard">Standard</MenuItem>
                  <MenuItem value="premium">Premium</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Organization Timezone</InputLabel>
                <Select
                  value={newOrg.timezone}
                  label="Organization Timezone"
                  onChange={(e) => setNewOrg({ ...newOrg, timezone: e.target.value })}
                >
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newOrg.trialWall}
                    onChange={(e) => setNewOrg({ ...newOrg, trialWall: e.target.checked })}
                  />
                }
                label="Trial Wall"
              />
            </Grid>
            {newOrg.trialWall && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Trial Duration (days)"
                  value={newOrg.trialDuration || ''}
                  onChange={(e) => setNewOrg({ ...newOrg, trialDuration: parseInt(e.target.value) || null })}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenOrgDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateOrg} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Priorities Dialog */}
      <Dialog open={openPriorityDialog} onClose={() => setOpenPriorityDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Priorities</DialogTitle>
        <DialogContent>
          <Table size="small" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>Priority</TableCell>
                <TableCell>Organization</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...organizations]
                .sort((a, b) => {
                  const ap = a.priority ?? 9999;
                  const bp = b.priority ?? 9999;
                  return ap - bp || (a.name || '').localeCompare(b.name || '');
                })
                .map(org => (
                  <TableRow key={org.id}>
                    <TableCell sx={{ width: 80 }}>
                      <TextField
                        size="small"
                        type="number"
                        value={priorityEdits[org.id] ?? ''}
                        onChange={(e) => setPriorityEdits(prev => ({ ...prev, [org.id]: e.target.value }))}
                        sx={{ width: 70 }}
                        inputProps={{ min: 0, style: { textAlign: 'center' } }}
                      />
                    </TableCell>
                    <TableCell>{org.name}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPriorityDialog(false)}>Cancel</Button>
          <Button onClick={handleSavePriorities} variant="contained" disabled={savingPriorities}>
            {savingPriorities ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminOrganizations;
